<template>
  <v-dialog
    no-click-animation
    persistent
    content-class="dialog"
    max-width="1140px"
    v-model="dialog"
    fullscreen
    :retain-focus="false"
    hide-overlay
    dark
    transition="dialog-bottom-transition">
    <v-dialog v-model="showFreeTrainingWelcome" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="welcome-overlay">
        <div class="welcome-content">
          <div class="emoji-line">💪</div>
          <h1 class="welcome-title">
            <span class="line1">Let's</span><br />
            <span class="line2">Go!</span>
          </h1>
          <p class="welcome-subtitle">Dein freies Training ist startklar. Füge jetzt deine erste Übung hinzu.</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="openAddExerciseFromWelcome">
            <v-icon left>mdi-plus-circle</v-icon>
            Erste Übung auswählen
          </v-btn>
        </div>
      </div>
    </v-dialog>
    <div class="workout-shell" :style="{ '--overview-accent': overviewAccentColor }">
      <header ref="workoutHeader" class="mainpage-top premium-header-shadow" :style="{ backgroundColor: currentBackgroundColorHex, color: getTextColorBasedOnBackground() }">
        <div class="header-glass-overlay" aria-hidden="true"></div>
      <v-row no-gutters align="center" class="px-4 py-2 header-row-base">
        <v-col cols="auto">
          <v-btn icon @click="onClose()" :ripple="false" class="glass-btn" aria-label="Zurück">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-chevron-left</v-icon>
          </v-btn>
        </v-col>
        <v-col class="overflow-hidden pl-2 text-center">
          <div
            v-if="supersetIndicator"
            class="caption font-weight-bold text-uppercase"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="opacity: 0.7; font-size: 0.65rem !important; letter-spacing: 1px">
            {{ supersetIndicator }}
          </div>
          <div
            class="font-weight-black text-truncate text-uppercase header-title"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="font-size: 1.1rem; letter-spacing: 0.5px; line-height: 1.2">
            {{ getSelectedExercise ? currentExerciseDisplayTitle : activeSplit ? activeSplit.title : 'Training' }}
          </div>
          <div
            v-if="getSelectedExercise && getSelectedExercise.deviceShortForm"
            class="caption"
            :class="[getTextColorBasedOnBackgroundClass()]"
            style="opacity: 0.8">
            {{ getSelectedExercise.deviceShortForm }}
          </div>
        </v-col>
        <v-col v-if="watchCompanionAvailable" cols="auto" class="px-0">
          <v-btn
            small
            rounded
            depressed
            class="watch-pulse-trigger"
            :style="{ color: watchCompanionIsLive || watchCompanion.status === 'error' ? '#fff' : getTextColorBasedOnBackground() }"
            aria-label="Watch-Pulstracking"
            :class="{ 'watch-pulse-trigger--live': watchCompanionIsLive }"
            :loading="watchCompanionIsBusy"
            :color="watchCompanionButtonColor"
            @click.stop="startWatchPulseTracking">
            <v-icon small :left="watchCompanionIsLive">mdi-watch-variant</v-icon>
            <span v-if="watchCompanionIsLive">{{ watchCompanionPulseText }}</span>
            <span v-else class="d-none d-sm-inline">Puls</span>
          </v-btn>
        </v-col>
        <v-col v-if="getSelectedExercise && !getSelectedExercise.isSuperset && !activeSplit.isHyrox" cols="2" class="text-right">
          <v-btn icon @click="openSwapDialog" title="Übung austauschen" class="glass-btn">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-swap-horizontal</v-icon>
          </v-btn>
        </v-col>
        <v-col v-else-if="!getSelectedExercise" cols="auto" class="d-flex flex-row justify-content-center">
          <v-btn icon @click="handleOpenAddDialog" title="Übung hinzufügen" class="glass-btn">
            <v-icon :color="getTextColorBasedOnBackground()">mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
        <div v-if="activeSplit && activeSplit.isHyrox && !getSelectedExercise" class="race-clock-container" :class="{ 'animate-in': raceClockStarted }">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>
        <div v-else-if="!isExpanded && !getSelectedExercise" class="collapsed-metrics px-4 pb-3 pt-1 d-flex justify-space-between align-center">
          <div class="metric-capsule capsule-kcal" title="Kalorien (Schätzung)" :class="{ 'light-theme': !isBackgroundDark() }">
            <v-icon x-small color="#ff7800" class="mr-1">mdi-fire</v-icon>
            <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ caloriesBurned }}</span>
            <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">kcal</span>
          </div>

          <div class="metric-capsule capsule-min" :class="{ 'light-theme': !isBackgroundDark() }">
            <v-icon x-small color="#00b4ff" class="mr-1">mdi-clock-outline</v-icon>
            <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ workoutMinutes }}</span>
            <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">Min</span>
          </div>

          <div class="metric-capsule capsule-kg" :class="{ 'light-theme': !isBackgroundDark() }">
            <v-icon x-small color="#00e678" class="mr-1">mdi-weight-lifter</v-icon>
            <span class="metric-val font-weight-bold" :class="[getTextColorBasedOnBackgroundClass()]">{{ liftedWeight }}</span>
            <span class="metric-unit" :class="[getTextColorBasedOnBackgroundClass()]">kg</span>
          </div>
        </div>
        <v-expand-transition>
          <button
            v-if="canUseWatchFeature && watchCompanion.status !== 'idle'"
            type="button"
            class="watch-companion-live-strip mx-3 mb-2"
            :class="`watch-zone-${watchCompanion.heartRateZone || 0}`"
            @click="startWatchPulseTracking">
            <span class="watch-live-icon">
              <v-icon small color="#ff4d5e">mdi-heart-pulse</v-icon>
            </span>
            <span class="watch-live-copy">
              <strong>{{ watchCompanionStatusTitle }}</strong>
              <small>{{ watchCompanionStatusDetail }}</small>
            </span>
            <span v-if="watchCompanionIsLive" class="watch-live-value">
              {{ watchCompanionPulseText }} <small>BPM</small>
            </span>
            <v-progress-circular v-else-if="watchCompanionIsBusy" indeterminate size="20" width="2" color="white" />
            <v-icon v-else small color="#ffb74d">mdi-alert-circle</v-icon>
          </button>
        </v-expand-transition>
        <div v-if="isExpanded && !getSelectedExercise" id="workout-overview" class="workout-overview-scroll" tabindex="0" aria-label="Trainingsübersicht scrollen">
          <run-workout-overview
            :progress="workoutProgress" :completed="completedExercisesCount" :total="totalExercisesCount"
            :minutes="workoutMinutes" :weight="liftedWeight" :calories="caloriesBurned"
            :next-exercise="nextExercise" :next-image="nextExercise ? getImageForExercise(nextExercise) : ''"
            :next-set-count="nextExerciseSetCount" :focus="categoryDistribution" :accent="overviewAccentColor"
            @next="openNextExercise" />
        </div>
        <button v-if="!getSelectedExercise && !(activeSplit && activeSplit.isHyrox)" type="button" class="overview-toggle"
          :aria-expanded="String(isExpanded)" aria-controls="workout-overview" @click="expandedFix = !expandedFix">
          <span>{{ isExpanded ? 'Zur Übungsliste' : 'Trainingsübersicht' }}</span>
          <span v-if="!isExpanded" class="overview-toggle-progress">{{ completedExercisesCount }}/{{ totalExercisesCount }}</span>
          <v-icon small :color="getTextColorBasedOnBackground()">{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </button>
      </header>
      <main class="workout-content-area">
        <WatchWorkoutSyncNotice />
        <run-workout-exercise-list ref="exerciseList" :header-height="headerHeight"
          @open-add-dialog="handleOpenAddDialog" @edit="handleEditExercise"
          @move="handleMoveExercise" @delete="handleDeleteExercise" />
      </main>
    </div>

    <ExerciseSelectionDialog
      :dialog.sync="selectionDialogActive"
      :mode="selectionDialogMode"
      :insertion-index="selectionDialogInsertionIndex"
      :exercise-to-swap="getSelectedExercise"
      :recommended-tags="swapRecommendedTags"
      @swap-selected="configureNewExercise($event.newExercise)"
      @add-selected="configureNewExercise($event.newExercise)" />
    <help-dialog :dialog.sync="isHelpDialogVisible" />

    <v-dialog v-model="configureDialogVisible" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card dark class="configure-dialog-card d-flex flex-column">
        <div class="configure-dialog-bg-glow"></div>
        <v-card-text class="pa-4 flex-grow-1 overflow-y-auto" style="position: relative; z-index: 1">
          <workout-split-edit-exercise-single
            v-if="exerciseToConfigure"
            :exercise-prop="exerciseToConfigure"
            @savedExercise="finalizeAction"
            @cancel-edit="cancelConfiguration" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showHyroxStartDialog" fullscreen hide-overlay persistent transition="fade-transition">
      <div class="hyrox-start-overlay" :class="{ 'start-transition': raceClockStarting }">
        <div class="race-clock-placeholder">
          <div class="race-clock-display">{{ raceClockDisplay }}</div>
        </div>

        <div class="hyrox-content">
          <h1 class="hyrox-main-title">
            <span class="char">H</span><span class="char">Y</span><span class="char">R</span><span class="char">O</span><span class="char">X</span>
          </h1>
          <p class="hyrox-subtitle">THE RACE IS ON</p>
          <v-btn x-large rounded depressed color="white" class="font-weight-bold black--text mt-8 start-btn" @click="startTheRace">
            <v-icon left>mdi-timer-play-outline</v-icon>
            START
          </v-btn>
        </div>
      </div>
    </v-dialog>
    <WorkoutTimer />
  </v-dialog>
</template>

<script>
import RunWorkoutOverview from "./RunWorkoutOverview.vue"
import nativeHeaderColor, { normalizeHeaderColor } from "@/services/nativeHeaderColor"
import RunWorkoutExerciseList from "./runWorkoutExerciseList.vue"
// NEU: Importiere die Komponente zum Bearbeiten der Sätze
import WorkoutSplitEditExerciseSingle from "../../WorkoutSplitEditExerciseSingle.vue" // BITTE PFAD PRÜFEN & ANPASSEN
import ExerciseSelectionDialog from "./runWorkoutExerciseSelectionDialog.vue" // Pfad ggf. anpassen
import HelpDialog from "./helpDialog.vue" // Pfad ggf. anpassen
import WorkoutTimer from "./WorkoutTimer.vue" // NEU: Importiere den Timer
import userService from "@/services/userService"
import watchSyncService from "@/services/watchSyncService"
import WatchWorkoutSyncNotice from "@/components/training/WatchWorkoutSyncNotice.vue"
import trainingFeatureAccess from "@/services/trainingFeatureAccess"
import trainingLiveService from "@/services/trainingLiveService"
import { store } from "@/store/stores"
import { workoutElapsedSeconds, isWorkoutExerciseComplete } from "@/services/workoutRecovery"
import { watchDeviceLabel } from "@/services/watchPlatform"

import { mapState, mapActions, mapGetters } from "vuex"

function getLuminance(hexColor) {
  const rgb = [1, 3, 5].map((i) => parseInt(hexColor.slice(i, i + 2), 16) / 255)
    .map((c) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]
}

export default {
  name: "Workingout",
  components: {
    HelpDialog, // HIER HINZUFÜGEN
    RunWorkoutOverview,
    WatchWorkoutSyncNotice,
    RunWorkoutExerciseList,
    ExerciseSelectionDialog,
    WorkoutSplitEditExerciseSingle, // NEU
    WorkoutTimer,
  },
  data() {
    return {
      raceClockStartTime: null, // NEU: Für präzise Zeitmessung
      raceClockMilliseconds: 0, // NEU: Wir messen in Millisekunden
      raceClockInterval: null,
      raceClockSeconds: 0,
      raceClockStarted: false, // Steuert die Sichtbarkeit der finalen Uhr
      raceClockStarting: false,
      showHyroxStartDialog: false, // NEU
      showFreeTrainingWelcome: false, // NEU

      isHelpDialogVisible: false, // HIER HINZUFÜGEN

      swapRecommendedTags: [], // NEU: Hält die empfohlenen Tags für den Austausch
      // Umbenannt für Klarheit
      selectionDialogActive: false,
      configureDialogVisible: false,

      // NEU: Steuert den Modus des Dialogs
      selectionDialogMode: "swap",
      selectionDialogInsertionIndex: null,
      configureDialogVisible: false, // NEU: Steuert den Konfigurations-Dialog
      exerciseToConfigure: null, // NEU: Hält die ausgewählte, aber noch nicht konfigurierte Übung
      dialog: true,
      headerHeight: 60,
      timerId: null,
      now: this.$moment(),
      expandedFix: false,
      forceLeaveRoute: false,
    }
  },
  computed: {
    sharedWatchWorkout() { return this.$store.state.runWorkoutStore.liveSession?.owner === "watch" },
    canUseWatchFeature() {
      return trainingFeatureAccess.isAllowed()
    },
    ...mapState("runWorkoutStore", [
      "workoutStartTime",
      "activeSplit",
      "selectedExerciseIndex",
      "completedSetsMap",
      "activeSupersetSubIndex",
      "activePlanID",
      "watchCompanion",
    ]),
    ...mapGetters("runWorkoutStore", ["getSelectedExercise", "getActiveSupersetSubExercise"]),
    // ... alle anderen computed properties bleiben unverändert
    isResumingWorkout() {
      return Object.values(this.completedSetsMap).some((sets) => sets.length > 0)
    },
    raceClockDisplay() {
      const totalSeconds = this.raceClockMilliseconds / 1000
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = Math.floor(totalSeconds % 60)
      const hundredths = Math.floor((this.raceClockMilliseconds % 1000) / 10) // Berechnet die Hundertstel

      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}:${String(hundredths).padStart(2, "0")}`
    },
    supersetIndicator() {
      if (this.getSelectedExercise?.isSuperset) {
        const currentIndex = this.activeSupersetSubIndex + 1
        const total = this.getSelectedExercise.exercises.length
        return `Supersatz (${currentIndex}/${total})`
      }
      return ""
    },
    watchCompanionIsLive() {
      return ["running", "paused"].includes(this.watchCompanion.status)
    },
    watchCompanionAvailable() {
      return this.canUseWatchFeature && (this.watchCompanion.status !== "idle" || watchSyncService.isAvailable())
    },
    watchCompanionIsBusy() {
      return ["starting", "waiting_for_sensor", "open_watch", "stopping", "discarding"].includes(
        this.watchCompanion.status,
      )
    },
    watchCompanionPulseText() {
      const value = Number(this.watchCompanion.currentHeartRate)
      return Number.isFinite(value) && value > 0 ? Math.round(value) : "--"
    },
    watchCompanionButtonColor() {
      if (this.watchCompanion.status === "error") return "orange darken-2"
      return this.watchCompanionIsLive ? "red darken-2" : "rgba(255,255,255,0.16)"
    },
    watchCompanionStatusTitle() {
      const device = watchDeviceLabel(null, this.watchCompanion.platform)
      const labels = {
        starting: "Watch wird verbunden",
        waiting_for_sensor: "Pulssensor startet",
        open_watch: "Watch öffnen",
        running: `${device} · Live`,
        paused: "Watch-Messung pausiert",
        stopping: "Messung wird beendet",
        ended: "Watch-Messung gespeichert",
        busy: "Anderes Watch-Training läuft",
        user_mismatch: "Watch-Benutzer prüfen",
        error: "Watch nicht verbunden",
      }
      return labels[this.watchCompanion.status] || device
    },
    watchCompanionStatusDetail() {
      if (this.watchCompanion.error) return this.watchCompanion.error
      if (this.watchCompanionIsLive) {
        const zone = Number(this.watchCompanion.heartRateZone)
        const zoneText = zone >= 1 && zone <= 5 ? `Zone ${zone}` : "Puls wird erfasst"
        const calories = Math.round(Number(this.watchCompanion.activeEnergyKilocalories) || 0)
        return `${zoneText} · ${calories} aktive kcal`
      }
      return "Roody auf der Watch öffnen, falls nötig"
    },
    currentExerciseDisplayTitle() {
      if (!this.getSelectedExercise) {
        return this.activeSplit.title
      }
      if (this.getSelectedExercise.isSuperset) {
        // Wenn Supersatz, nimm den Titel der aktiven Sub-Übung
        return this.getActiveSupersetSubExercise?.title || "Supersatz"
      }
      // Ansonsten den normalen Titel
      return this.getSelectedExercise.title
    },
    workoutMinutes() {
      if (!this.workoutStartTime) return 0
      return Math.floor(workoutElapsedSeconds(this.$store.state.runWorkoutStore, this.now.valueOf()) / 60)
    },
    isExpanded() {
      return this.expandedFix && !this.activeSplit?.isHyrox
    },
    caloriesBurned() {
      return Math.round(this.workoutMinutes * 7 * 10) / 10
    },
    // KORRIGIERT: Greift jetzt auf completedSetsMap zu.
    liftedWeight() {
      if (!this.completedSetsMap) return 0
      const allSets = Object.values(this.completedSetsMap).flat()
      const totalWeight = allSets.reduce((total, set) => {
        if (!Array.isArray(set) || set.some((s) => s.skipped)) {
          return total
        }
        const weight = set.find((item) => item.text === "Gewicht")?.value || 0
        const reps = set.find((item) => /Wiederholungen/.test(item.text))?.value || 0
        return total + Number(weight) * Number(reps)
      }, 0)
      return Math.round(totalWeight)
    },
    currentBackgroundColorHex() {
      if (this.activeSplit?.isHyrox) return "#141414"
      if (this.getSelectedExercise?.warmup && !this.getSelectedExercise?.sets?.length) return "#FFA500"
      if (this.getSelectedExercise) return "#292929"
      return normalizeHeaderColor(this.activeSplit?.fixedVisualization?.color)
    },
    // --- NEUE COMPUTED PROPERTIES FÜR EXPANDED HEADER ---
    totalExercisesCount() {
      return this.activeSplit?.exercises?.length || 0
    },
    completedExercisesCount() {
      return (this.activeSplit?.exercises || []).filter((exercise, index) =>
        isWorkoutExerciseComplete(exercise, this.completedSetsMap[index] || []),
      ).length
    },
    workoutProgress() {
      if (this.totalExercisesCount === 0) return 0
      return (this.completedExercisesCount / this.totalExercisesCount) * 100
    },
    nextExercise() {
      return (this.activeSplit?.exercises || []).find((exercise, index) =>
        !isWorkoutExerciseComplete(exercise, this.completedSetsMap[index] || []),
      ) || null
    },
    nextExerciseSetCount() {
      const next = this.nextExercise
      if (!next) return 0
      return next.isSuperset ? (next.exercises || []).reduce((sum, sub) => sum + (sub.sets?.length || 0), 0) : next.sets?.length || 0
    },
    nativeHeaderColor() {
      if (this.showHyroxStartDialog) return "#000000"
      if (this.showFreeTrainingWelcome || this.selectionDialogActive || this.configureDialogVisible) return "#121212"
      return this.currentBackgroundColorHex
    },
    overviewAccentColor() {
      const hex = normalizeHeaderColor(this.activeSplit?.fixedVisualization?.color, "#00963f")
      const { r, g, b } = this.hexToRgb(hex)
      return `rgb(${[r, g, b].map((v) => Math.round(v * 0.5 + 127.5)).join(", ")})`
    },
    // Planned muscle assignments, not a measurement of training intensity.
    categoryDistribution() {
      const categories = { Beine: 0, Push: 0, Pull: 0, Core: 0, Cardio: 0 }

      if (this.activeSplit && this.activeSplit.exercises) {
        this.activeSplit.exercises.flatMap((ex) => ex.isSuperset ? ex.exercises || [] : [ex]).forEach((ex) => {
          if (Array.isArray(ex.tags)) {
            ex.tags.forEach((t) => {
              // Wir nutzen die 'class' Eigenschaft aus deinen Tags für Robustheit
              const tagKey = String(t && typeof t === "object" ? t.class || t.name || t.title || "" : t || "").toLowerCase()

              // MAPPING LOGIK
              if (
                [
                  "oberschenkel",
                  "wade",
                  "gesaess",
                  "beinbeuger",
                  "schienbein",
                  "scholle",
                  "adduktoren",
                  "abduktoren",
                  "quadriceps",
                  "hamstrings",
                  "calves",
                  "glutes",
                ].some((k) => tagKey.includes(k))
              ) {
                categories["Beine"] += 1
              } else if (
                ["brust", "trizeps", "vordere-schulter", "seitliche-schulter", "chest", "triceps", "front-delt", "side-delt", "push"].some((k) =>
                  tagKey.includes(k),
                )
              ) {
                categories["Push"] += 1
              } else if (
                [
                  "latissimus",
                  "bizeps",
                  "trapez",
                  "hintere-schulter",
                  "ruecken-strecker",
                  "nacken",
                  "unterarm",
                  "back",
                  "biceps",
                  "rear-delt",
                  "pull",
                ].some((k) => tagKey.includes(k))
              ) {
                categories["Pull"] += 1
              } else if (["bauch", "gerader-bauch", "seitlicher-bauch", "abs", "core"].some((k) => tagKey.includes(k))) {
                categories["Core"] += 1
              } else if (["herz-kreislauf", "cardio", "running", "rowing"].some((k) => tagKey.includes(k))) {
                categories["Cardio"] += 1
              }
            })
          }
        })
      }
      return categories
    },
  },
  methods: {
    openWatchExerciseRequest() {
      const id = this.$route.query.addExercise
      if (!id || id !== this.$store.state.runWorkoutStore.liveSession?.clientSessionId) return
      this.handleOpenAddDialog()
      const query = { ...this.$route.query }; delete query.addExercise
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    ...mapActions("runWorkoutStore", [
      "resetWorkoutStartTime",
      "setSelectedExerciseIndex",
      "swapExerciseTemporarily",
      "addExerciseTemporarily",
      "updateExerciseInSplit", // NEU
      "moveExercise", // NEU
      "removeExerciseTemporarily",
    ]),
    async startWatchPulseTracking() {
      if (!this.canUseWatchFeature || this.sharedWatchWorkout) return
      if (this.watchCompanionIsLive) {
        this.$toast.success(`Watch-Pulstracking läuft mit ${this.watchCompanionPulseText} BPM`)
        return
      }
      if (this.watchCompanionIsBusy) return

      try {
        await watchSyncService.startPhoneCompanionWorkout({
          clientSessionId: this.watchCompanion.clientSessionId,
          title: this.activeSplit?.title || "Training",
          workoutId: this.activePlanID || this.activeSplit?.planID || null,
          splitKey: this.activeSplit?._id || this.activeSplit?.title || null,
          planRevision: this.activeSplit?.updatedAt || null,
          startedAt: this.workoutStartTime,
          healthActivityType: this.activeSplit?.isHyrox
            ? "crossTraining"
            : "traditionalStrengthTraining",
        })
        this.$toast.success(`Pulstracking auf der ${watchDeviceLabel(null, this.watchCompanion.platform)} gestartet`)
      } catch (error) {
        this.$toast.error(error?.message || "Watch konnte nicht gestartet werden")
      }
    },
    startTheRace() {
      this.raceClockStarting = true // Startet die Übergangs-Animation
      // Wir geben der Animation etwas mehr Zeit, um sauber auszusehen
      setTimeout(() => {
        this.showHyroxStartDialog = false
        this.raceClockStarted = true
        this.startRaceClock()
      }, 600) // Dauer auf 1.2s erhöht
    },
    startRaceClock() {
      if (this.raceClockInterval) clearInterval(this.raceClockInterval)

      const updateClock = () => {
        const now = this.$moment()
        const elapsed = Math.max(0, workoutElapsedSeconds(this.$store.state.runWorkoutStore, now.valueOf()) - 3) * 1000
        this.raceClockMilliseconds = Math.max(0, elapsed)
      }

      updateClock() // Einmal sofort ausführen, um die korrekte Zeit sofort anzuzeigen
      this.raceClockInterval = setInterval(updateClock, 51) // Hochfrequentes Update für flüssige Hundertstel
    },
    openAddExerciseFromWelcome() {
      this.showFreeTrainingWelcome = false
      this.handleOpenAddDialog()
    },
    // NEU: Editieren einer Übung
    handleEditExercise(exercise) {
      // Wir nutzen denselben Flow wie beim Adden, aber mit Initialdaten
      this.selectionDialogMode = "edit"
      // Finde den Index
      const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
      if (idx === -1) return

      this.setSelectedExerciseIndex(idx) // KORREKT: Über den Store gehen
      this.exerciseToConfigure = JSON.parse(JSON.stringify(exercise)) // Deep Copy
      this.configureDialogVisible = true
    },
    // NEU: Verschieben einer Übung
    handleMoveExercise({ exercise, direction }) {
      const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
      if (idx === -1) return

      const newIndex = direction === "up" ? idx - 1 : idx + 1
      if (newIndex < 0 || newIndex >= this.activeSplit.exercises.length) return

      this.moveExercise({ fromIndex: idx, toIndex: newIndex })
    },
    // NEU: Entfernen einer Übung
    handleDeleteExercise(exercise) {
      if (confirm(`Möchtest du die Übung "${exercise.title}" wirklich aus dem Trainingsplan entfernen?`)) {
        const idx = this.activeSplit.exercises.findIndex((e) => e._id === exercise._id)
        if (idx === -1) return
        this.removeExerciseTemporarily(idx)
      }
    },
    updateLiveActivity() {
      trainingLiveService.schedule()
    },
    getNativeAssetName(imagePath) {
      if (!imagePath) return "default_split_image" // Fallback-Bildname
      // Extrahiert 'mein_bild' aus 'pfad/zu/mein_bild.png'
      return imagePath.split("/").pop().split(".")[0]
    },
    // --- NEUE UND ANGEPASSTE METHODEN FÜR DEN SWAP-PROZESS ---
    openSwapDialog() {
      if (!this.getSelectedExercise) return
      const currentExerciseInPlan = this.activeSplit.exercises[this.selectedExerciseIndex]
      this.swapRecommendedTags = Array.isArray(currentExerciseInPlan.recommendedTags) ? currentExerciseInPlan.recommendedTags : []

      this.selectionDialogMode = "swap"
      this.selectionDialogActive = true
    },

    // Wird vom Kind-Component per Event aufgerufen
    handleOpenAddDialog() {
      this.selectionDialogMode = "add"
      this.selectionDialogInsertionIndex = this.activeSplit.exercises.length // Füge am Ende des Plans hinzu
      this.swapRecommendedTags = [] // Keine Empfehlungen beim Hinzufügen
      this.selectionDialogActive = true
    },

    configureNewExercise(newExercise) {
      this.selectionDialogActive = false
      this.exerciseToConfigure = JSON.parse(JSON.stringify(newExercise))
      this.configureDialogVisible = true
    },

    // Diese Methode schliesst den gesamten Vorgang ab
    finalizeAction(configuredExercise) {
      if (this.selectionDialogMode === "swap") {
        if (this.selectedExerciseIndex === null) return
        this.swapExerciseTemporarily({
          exerciseIndex: this.selectedExerciseIndex,
          newExercise: configuredExercise,
        })
        this.$toast.success(`${configuredExercise.title} wurde erfolgreich ausgetauscht!`)
      } else if (this.selectionDialogMode === "edit") {
        // NEU: Edit Mode
        if (this.selectedExerciseIndex === null) return
        this.updateExerciseInSplit({
          exerciseIndex: this.selectedExerciseIndex,
          newExercise: configuredExercise,
        })
        this.$toast.success(`${configuredExercise.title} wurde aktualisiert!`)
      } else {
        // mode === 'add'
        this.addExerciseTemporarily({
          newExercise: configuredExercise,
          atIndex: this.selectionDialogInsertionIndex,
        })
        this.$toast.success(`${configuredExercise.title} wurde dem Plan hinzugefügt!`)
      }
      this.cancelConfiguration()
    },

    cancelConfiguration() {
      this.configureDialogVisible = false
      this.exerciseToConfigure = null
      this.selectionDialogInsertionIndex = null
    },
    isBackgroundDark(hexColor = this.currentBackgroundColorHex) {
      if (this.activeSplit?.isHyrox) {
        return true
      }
      const luminance = getLuminance(hexColor)
      return luminance < 0.179
    },
    getTextColorBasedOnBackground() {
      return this.isBackgroundDark() ? "white" : "black"
    },
    getTextColorBasedOnBackgroundClass() {
      return this.isBackgroundDark() ? "white--text" : "black--text"
    },
    updateHeaderColorForFlutter() {
      if (this._headerColorOwner) nativeHeaderColor.setOverride(this._headerColorOwner, this.nativeHeaderColor)
    },
    measureHeader() {
      const height = Math.ceil(this.$refs.workoutHeader?.getBoundingClientRect().height || 0)
      if (height !== this.headerHeight) this.headerHeight = height
    },
    openNextExercise() {
      const index = this.activeSplit?.exercises.indexOf(this.nextExercise) ?? -1
      if (index < 0) return
      this.expandedFix = false
      this.$refs.exerciseList?.open({ ...this.nextExercise, originalIndex: index })
    },
    async onClose() {
      if (this.getSelectedExercise) {
        this.setSelectedExerciseIndex(-1)
        this.forceLeaveRoute = false
      } else {
        this.dialog = false
        this.forceLeaveRoute = true
        this.$router.replace({ name: "Workouts_v2" }) // Ziel anpassen falls nötig
      }
    },
    hexToRgb(hex) {
      hex = hex.replace(/^#/, "")
      if (hex.length === 3)
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("")
      if (hex.length !== 6) return { r: 0, g: 0, b: 0 }
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return { r, g, b }
    },
    getImageForExercise(exercise) {
      if (exercise.picture) return this.getImageUrl(exercise.picture)
      if (exercise.manual && exercise.manual[0]?.picture) return this.getImageUrl(exercise.manual[0].picture)
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    getImageUrl(imageId) {
      if (!imageId) return ""
      return `/img/uebungen/${imageId.replace(/\.[^.]+$/, "")}.webp`
    },
  },
  created() {
    this._headerColorOwner = {}
    this.updateHeaderColorForFlutter()
    if (!this.workoutStartTime) {
      this.resetWorkoutStartTime()
    }
  },
  // In Workingout.vue -> mounted()
  async mounted() {
    this.$store.dispatch("runWorkoutStore/fetchGlobalWeightStatistics")

    this.timerId = setInterval(() => {
      this.now = this.$moment()
      this.updateLiveActivity()
    }, 15000) // Update alle 15 Sekunden ist ein guter Kompromiss

    // --- START: NEUE LOGIK ---
    if (this.activeSplit) {
      const titleLower = this.activeSplit.title.toLowerCase()
      if (titleLower.includes("hyrox")) {
        // Speziell für Hyrox den neuen Dialog anzeigen
        if (this.isResumingWorkout) {
          this.raceClockStarted = true
          this.startRaceClock()
        } else {
          this.showHyroxStartDialog = true
        }
      } else if (this.activeSplit.isFreeTraining && !this.sharedWatchWorkout) {
        // Den alten Dialog nur noch für NICHT-Hyrox freie Trainings anzeigen
        this.showFreeTrainingWelcome = true
      }
    }

    this.$nextTick(() => {
      this.measureHeader()
      if (this.$refs.workoutHeader && typeof ResizeObserver !== "undefined") {
        this._headerResizeObserver = new ResizeObserver(this.measureHeader)
        this._headerResizeObserver.observe(this.$refs.workoutHeader)
      }
    })
    window.addEventListener("resize", this.measureHeader)

    this.openWatchExerciseRequest()
    trainingLiveService.resume()
    trainingLiveService.schedule()
  },
  async beforeRouteEnter(to, from, next) {
    await store.restored
    const destination = trainingLiveService.entryRoute()
    next(destination || undefined)
  },
  beforeRouteLeave(to, from, next) {
    if (to.path === "/workout/hub" && to.query.recover === this.$store.state.runWorkoutStore.liveSession?.clientSessionId) {
      next()
      return
    }
    if (to.name === "WorkoutSummary") {
      next()
      return
    }
    if (this.forceLeaveRoute) {
      next()
      return
    }
    this.onClose()
    next(false)
  },
  beforeDestroy() {
    clearInterval(this.timerId)
    clearInterval(this.raceClockInterval) // NEU
    this._headerResizeObserver?.disconnect()
    window.removeEventListener("resize", this.measureHeader)
    nativeHeaderColor.clearOverride(this._headerColorOwner)
  },
  updated() {
    this.measureHeader()
  },
  watch: {
    "$route.query.addExercise"() { this.openWatchExerciseRequest() },
    "$store.state.runWorkoutStore.liveSession.phase"(phase) {
      if (this.sharedWatchWorkout && ["ending", "completed", "discarded"].includes(phase)) this.$router.replace({ name: "WorkoutSummary" }).catch(() => {})
    },
    getSelectedExercise() {
      this.expandedFix = false
    },
    nativeHeaderColor() {
      this.updateHeaderColorForFlutter()
    },
  },
}
</script>
<style lang="scss" scoped>
.configure-dialog-card {
  background-color: #121212 !important;
  color: white;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.configure-dialog-bg-glow {
  position: absolute;
  width: 100%;
  height: 50%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 50% 0%, rgba(33, 150, 243, 0.1), transparent 75%);
  pointer-events: none;
  z-index: 0;
}

.workout-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  background: #121212;
  font-family: "Inter", -apple-system, sans-serif;
}
.mainpage-top {
  position: relative;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-height: 0;
  max-height: calc(100% - 112px);
  overflow: hidden;
  border-radius: 0 0 24px 24px;
}
.header-row-base { flex: 0 0 auto; min-height: 60px; flex-wrap: nowrap; width: 100%; }
.workout-overview-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; background: #121b17; }
.overview-toggle { display: flex; flex: 0 0 auto; width: 100%; align-items: center; justify-content: center; gap: 8px; padding: 10px 16px; min-height: 40px; color: inherit; font-size: 11px; font-weight: 600; border-top: 1px solid currentColor; border-top-color: rgba(128,128,128,.2); }
.overview-toggle-progress { opacity: .65; font-variant-numeric: tabular-nums; }
.overview-toggle:focus-visible { outline: 2px solid currentColor; outline-offset: -4px; }
.workout-content-area { flex: 1 1 0; min-height: 0; overflow: hidden; background: #121212; }
/* PREMIUM HEADER STYLES */
.premium-header-shadow {
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5) !important;
}

.header-glass-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(-45deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  background-size: 400% 400%;
  pointer-events: none;
  z-index: 1;
}

.glass-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border-radius: 50%;
  width: 36px !important;
  height: 36px !important;
}

.glass-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  transform: scale(1.08);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25), 0 0 10px rgba(var(--split-color-rgb, 255, 255, 255), 0.2);
}

.glass-btn:active {
  transform: scale(0.95);
}

.header-title {
  font-family: "Outfit", "Inter", -apple-system, sans-serif !important;
  font-size: 1.15rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.metric-capsule {
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  flex: 1 1 0;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.metric-capsule.light-theme {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

/* Individual capsule styles */
.metric-capsule.capsule-kcal {
  border-color: rgba(255, 120, 0, 0.25);
}
.metric-capsule.capsule-kcal:hover {
  background: rgba(255, 120, 0, 0.1);
  border-color: rgba(255, 120, 0, 0.5);
  box-shadow: 0 4px 15px rgba(255, 120, 0, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-capsule.capsule-min {
  border-color: rgba(0, 180, 255, 0.25);
}
.metric-capsule.capsule-min:hover {
  background: rgba(0, 180, 255, 0.1);
  border-color: rgba(0, 180, 255, 0.5);
  box-shadow: 0 4px 15px rgba(0, 180, 255, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-capsule.capsule-kg {
  border-color: rgba(0, 230, 120, 0.25);
}
.metric-capsule.capsule-kg:hover {
  background: rgba(0, 230, 120, 0.1);
  border-color: rgba(0, 230, 120, 0.5);
  box-shadow: 0 4px 15px rgba(0, 230, 120, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.metric-val {
  font-size: 0.95rem;
  margin-right: 3px;
  letter-spacing: -0.2px;
  font-weight: 800;
}

.metric-unit {
  font-size: 0.7rem;
  opacity: 0.8;
  font-weight: 700;
  text-transform: uppercase;
}

.mainpage-top > :not(.header-glass-overlay) { position: relative; z-index: 2; }
.collapsed-metrics { flex: 0 0 auto; gap: 8px; }
@media (max-width: 380px) {
  .metric-capsule { padding: 8px 7px; }
  .metric-val { font-size: 0.85rem; }
  .metric-unit { font-size: 0.65rem; }
}
@media (prefers-reduced-motion: reduce) {
  .glass-btn, .metric-capsule { transition: none !important; }
  .glass-btn:hover, .glass-btn:active, .metric-capsule:hover { transform: none; }
}

.welcome-overlay {
  background: radial-gradient(circle at center, #2a2a2a, #121212);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 24px;
  height: 100vh;
  overflow: hidden;
  /* NEU: Der ganze Hintergrund blendet sanft ein */
  animation: overlay-fade-in 0.5s ease-out forwards;
}

.welcome-content > * {
  /* Stellt sicher, dass alle Kind-Elemente unsichtbar starten */
  opacity: 0;
}

.emoji-line {
  font-size: 5rem;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.7);
  animation: pop-in 0.8s 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.welcome-title {
  font-size: 6rem;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.2), 0 0 40px rgba(255, 255, 255, 0.5);
  letter-spacing: -0.05em;
  /* Wichtig, damit die Kind-Animationen funktionieren */
  animation: fade-in 0.1s 0.6s forwards;
}

.welcome-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(60px) rotate(8deg);
  /* NEU: Weichere und deutlichere Animation */
  animation: reveal-text 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.welcome-title .line1 {
  animation-delay: 0.8s;
}
.welcome-title .line2 {
  animation-delay: 0.9s;
}

.welcome-subtitle {
  font-size: 1.1rem;
  max-width: 350px;
  margin: 24px auto 0;
  color: #b0b0b0;
  animation: fade-in-up 0.8s 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

.start-btn {
  animation: fade-in-up 0.8s 1.4s cubic-bezier(0.19, 1, 0.22, 1) forwards, pulse-shadow 2.5s 2s infinite ease-in-out;
  box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
}

/* --- KEYFRAME ANIMATIONEN FÜR DEN "WOW-FAKTOR" --- */

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes reveal-text {
  from {
    opacity: 0;
    transform: translateY(60px) rotate(8deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
}

@keyframes pulse-shadow {
  0% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
  50% {
    box-shadow: 0 0 40px 8px rgba(var(--v-primary-base-rgb), 0.6);
  }
  100% {
    box-shadow: 0 0 25px rgba(var(--v-primary-base-rgb), 0.4);
  }
}

/* In runWorkout.vue -> <style> */

/* --- START: EPISCHES STYLING FÜR HYROX-START --- */

.hyrox-start-overlay {
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.hyrox-start-overlay::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 150%;
  padding-bottom: 150%;
  background: radial-gradient(circle, rgba(255, 237, 0, 0.2) 0%, transparent 50%);
  transform-origin: center;
  transform: translate(-50%, -50%) scale(0);
  animation: background-pulse 2s 0.5s forwards;
}

.hyrox-content {
  position: relative;
  z-index: 2;
}

.hyrox-main-title {
  font-size: 10rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.1em;
  color: #fff;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.5), 0 0 30px #ffed00;
}

.hyrox-main-title .char {
  display: inline-block;
  opacity: 0;
  transform: translateY(-80px) scale(0.5);
  animation: letter-drop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

/* Staggered animation für die Buchstaben */
.hyrox-main-title .char:nth-child(1) {
  animation-delay: 0.8s;
}
.hyrox-main-title .char:nth-child(2) {
  animation-delay: 0.9s;
}
.hyrox-main-title .char:nth-child(3) {
  animation-delay: 1s;
}
.hyrox-main-title .char:nth-child(4) {
  animation-delay: 1.1s;
}
.hyrox-main-title .char:nth-child(5) {
  animation-delay: 1.2s;
}

.hyrox-subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  color: #ffed00;
  text-transform: uppercase;
  margin-top: 1rem;
  opacity: 0;
  animation: fade-in-up 1s 1.5s forwards;
}

.hyrox-content .start-btn {
  opacity: 0;
  animation: fade-in-up 1s 1.8s forwards;
}

@keyframes background-pulse {
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
}

@keyframes letter-drop {
  from {
    opacity: 0;
    transform: translateY(-80px) scale(0.5) rotate(-45deg);
    filter: blur(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
    filter: blur(0);
  }
}

.race-clock-container {
  flex: 0 0 auto;
  padding: 8px 16px 16px;
  text-align: center;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.4s 0.2s ease, transform 0.4s 0.2s ease;
}
.race-clock-container.animate-in {
  opacity: 1;
  transform: translateY(0);
}
.race-clock-display {
  font-family: "Teko", sans-serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: #ffed00;
  text-shadow: 0 0 10px rgba(255, 237, 0, 0.7);
  line-height: 1;
}

/* --- NEUE LOGIK FÜR DEN ÜBERGANG --- */

/* Die Platzhalter-Uhr, die genau dort sitzt, wo die echte Uhr sein wird */
.race-clock-placeholder {
  position: absolute;
  top: 60px; /* Höhe des Titel-Headers */
  left: 0;
  right: 0;
  height: 120px; /* Höhe des gesamten Headers */
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0; /* Startet unsichtbar */
}
.hyrox-start-overlay.start-transition .race-clock-placeholder {
  animation: fade-in 0.5s 0.7s forwards; /* Blendet sich am Ende ein */
}

/* Der Titel wird jetzt einfach kleiner und blendet aus */
.hyrox-start-overlay.start-transition .hyrox-main-title {
  animation: scale-and-fade-out 0.8s cubic-bezier(0.5, 0, 0.2, 1) forwards;
}

/* Das Overlay selbst blendet am Ende aus */
.hyrox-start-overlay.start-transition {
  animation: fade-out-bg 0.4s 0.8s forwards;
}

/* Die anderen Elemente blenden früher aus */
.hyrox-start-overlay.start-transition .hyrox-subtitle,
.hyrox-start-overlay.start-transition .start-btn {
  animation: fade-out-down 0.3s ease-out forwards;
}

@keyframes scale-and-fade-out {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.2);
    opacity: 0;
  }
}
@keyframes fade-out-bg {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fade-out-down {
  to {
    opacity: 0;
    transform: translateY(50px);
  }
}

.title-font {
  font-size: 1.4rem;
  line-height: 1.2;
}

.caption-font {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.opacity-70 {
  opacity: 0.7;
}

.filter-glow {
  filter: drop-shadow(0 0 8px currentColor);
}

.transparent-carousel .v-carousel__controls {
  background: transparent !important;
}

.watch-pulse-trigger {
  min-width: 38px !important;
  padding: 0 9px !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  backdrop-filter: blur(12px);
}

.watch-pulse-trigger--live {
  box-shadow: 0 0 0 1px rgba(255, 77, 94, 0.32), 0 0 18px rgba(255, 77, 94, 0.3) !important;
}

.watch-companion-live-strip {
  width: calc(100% - 24px);
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  color: #fff;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  background: linear-gradient(110deg, rgba(24, 24, 26, 0.9), rgba(69, 13, 23, 0.78));
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.26);
  backdrop-filter: blur(18px);
}

.watch-live-icon {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: rgba(255, 77, 94, 0.14);
}

.watch-live-copy {
  min-width: 0;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  line-height: 1.15;
}

.watch-live-copy strong {
  overflow: hidden;
  font-size: 0.72rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.watch-live-copy small {
  overflow: hidden;
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 0.59rem;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.watch-live-value {
  flex: 0 0 auto;
  color: #fff;
  font-size: 1.18rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.watch-live-value small {
  color: #ff8b98;
  font-size: 0.55rem;
  letter-spacing: 0.04em;
}

.watch-zone-1 { border-left: 3px solid #37d7ff; }
.watch-zone-2 { border-left: 3px solid #00c763; }
.watch-zone-3 { border-left: 3px solid #ffd54f; }
.watch-zone-4 { border-left: 3px solid #ff9800; }
.watch-zone-5 { border-left: 3px solid #ff3b4d; }
</style>
